import { useLocalListSearchParamState, useLocalSearchParamState } from '@Hooks/useLocalSearchParamState';
import { TableProps } from '@tlx/atlas';

export type OrderByType = OrderByTypeEmployeeOverview | OrderByTypeCreateSalary | OrderByTypeSalaryOverview;

export type OrderByTypeEmployeeOverview =
    | OrderByTypeEmployeeOverviewDetails
    | OrderByTypeEmployeeOverviewSalary
    | OrderByTypeEmployeeOverviewTaxcard;

export type OrderByTypeEmployeeOverviewDetails =
    | 'DISPLAYNAME'
    | 'NUMBER'
    | 'FIRSTNAME'
    | 'DEPARTMENT'
    | 'EMAIL'
    | 'PHONE'
    | 'PHONE_WORK'
    | 'ADDRESS'
    | 'DATE_OF_BIRTH'
    | 'RATE'
    | 'HOUR_COST_RATE'
    | 'ALLOW_LOGIN'
    | 'VISMA_2FA_CONNECT'
    | 'USER_ADMINISTRATOR'
    | 'ALLOW_REG_INFO'
    | 'EMPLOYEE_APPROVER';

export type OrderByTypeEmployeeOverviewSalary =
    | 'DISPLAYNAME'
    | 'NUMBER'
    | 'FIRSTNAME'
    | 'NATIONAL_IDENTITY_NUMBER'
    | 'STARTDATE'
    | 'DEPARTMENT'
    | 'DIVISION'
    | 'PERCENT_OF_EMPLOYMENT'
    | 'ANNUAL_SALARY'
    | 'MONTHLY_SALARY'
    | 'HOURLY_WAGE'
    | 'DELIVERY_METHOD_WAGE_SLIP'
    | 'BANK_ACCOUNT_NUMBER'
    | 'MUNICIPALITY_ENI'
    | 'OCCUPATION_CODE'
    | 'REMUNERATION_TYPE'
    | 'EMPLOYMENT_TYPE'
    | 'EMPLOYMENT_FORM';

export type OrderByTypeEmployeeOverviewTaxcard =
    | 'DISPLAYNAME'
    | 'NUMBER'
    | 'FIRSTNAME'
    | 'STATUS'
    | 'DEDUCTION_CODE'
    | 'TYPE'
    | 'TABLE_NUMBER'
    | 'PERCENTAGE_RATE'
    | 'FREE_CARD_AMOUNT'
    | 'ADDITIONAL_INFO'
    | 'LAST_CHANGED';

export type OrderByTypeCreateSalary =
    | 'DEFAULT'
    | 'NUMBER'
    | 'FIRSTNAME'
    | 'STARTDATE'
    | 'DEPARTMENT'
    | 'DIVISION'
    | 'STATUS'
    | 'PREVIOUS_SALARIES';

export type OrderByTypeSalaryOverview =
    | 'id'
    | 'datePeriod'
    | 'comment'
    | 'voucherDate'
    | 'paymentDate'
    | 'voucherNumberAsString'
    | 'ameldingStatus'
    | 'ameldingWageId'
    | 'completedLogId'
    | 'wagePaymentTransactionId'
    | 'wagePeriodTransactionId'
    | 'wagePeriodTransactionVoucherNumber'
    | 'payrollTaxBasisAmount'
    | 'approvedBy';

export type ColumnType = {
    id: OrderByType;
    label: string;
    width?: string;
    align?: 'left' | 'right';
    defaultDisabled?: boolean;
};

export interface OrderingState<OT> {
    sortColumn: OT;
    sortDirection: 'ascending' | 'descending' | undefined;
    getSortDirection: (column: OT) => 'ascending' | 'descending' | undefined;
    onChangeHandleSort: (column: OT) => void;
}

function getNewSortDirection(sortDirection: string | undefined) {
    if (sortDirection === 'ascending') {
        return 'descending';
    }
    if (sortDirection === 'descending') {
        return undefined;
    }
    return 'ascending';
}

export interface TableSortState<T extends string> {
    sortColumn: T;
    sortDirection: 'ascending' | 'descending' | undefined;
    getSortDirection: (column: string) => 'ascending' | 'descending';
    onSortChange: TableProps['onSortChange'];
    sort: TableProps['sort'];
}

export function useTableSorting<T extends string>(): TableSortState<T> {
    const [sortDirection, setSortDirection] = useLocalSearchParamState('sortDirection', '');
    const [sortColumn, setSortColumn] = useLocalSearchParamState('sortColumn', '');

    const getSortDirection = (column: string) => {
        return column === sortColumn ? sortDirection : undefined;
    };

    return <TableSortState<T>>{
        sortColumn,
        sortDirection,
        sort:
            sortDirection === undefined
                ? undefined
                : {
                      column: sortColumn,
                      direction: sortDirection,
                  },

        onSortChange: (sort) => {
            // Don't know what this is necessary for
            if (sort === undefined) {
                return;
            }
            const columnSortDirection = getSortDirection(sort.column);
            const newDirection = getNewSortDirection(columnSortDirection);
            if (newDirection) {
                setSortColumn(sort.column);
                setSortDirection(newDirection);
            } else {
                setSortColumn('');
                setSortDirection('');
            }
        },
    };
}

export function useOrdering<OT extends string>(): OrderingState<OT> {
    const [listOrderBy, setListOrderBy] = useLocalListSearchParamState('orderBy', ['', '']);
    const onChangeHandleSort = (column: OT) => {
        if (column === 'DEFAULT') {
            setListOrderBy(['', '']);
            return;
        }
        if (listOrderBy[0] !== column) {
            setListOrderBy([column, 'ASC']);
        } else if (listOrderBy[1] === 'ASC') {
            setListOrderBy([column, 'DESC']);
        } else {
            setListOrderBy(['', '']);
        }
    };

    const getSortDirection = (column: OT) => {
        if (listOrderBy[0] !== column) {
            return undefined;
        }
        return listOrderBy[1] === 'DESC' ? 'descending' : 'ascending';
    };

    return {
        sortColumn: listOrderBy[0] as OT,
        sortDirection: listOrderBy[1] as 'ascending' | 'descending' | undefined,
        getSortDirection,
        onChangeHandleSort,
    };
}
